import { reportPerformanceMetric } from "@/modules/performance_metrics";

const cache = new Map();
const pendingRequests = new Map();
const invalidatedAt = new Map();

const DEFAULT_TTL_MS = 60_000;
const CACHE_HIT_SAMPLE_RATE = 0.1;
export const CACHE_INVALIDATED_EVENT = "api-cache:invalidated";

const normalizeParams = (params = {}) =>
  Object.entries(params)
    .filter(([, value]) => value !== undefined && value !== null && value !== "")
    .sort(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey))
    .map(([key, value]) => [key, String(value)]);

export const buildCacheKey = (apiPath, params = {}) => {
  const queryParams = new URLSearchParams(normalizeParams(params)).toString();
  return `${apiPath}${queryParams ? `?${queryParams}` : ""}`;
};

const matchesPrefix = (cacheKey, apiPath) =>
  apiPath === null || cacheKey === apiPath || cacheKey.startsWith(`${apiPath}?`) || cacheKey.startsWith(`${apiPath}/`);

const wasInvalidatedAfterRequest = (cacheKey, startedAt) => {
  for (const [apiPath, invalidatedAtTimestamp] of invalidatedAt.entries()) {
    if (invalidatedAtTimestamp > startedAt && matchesPrefix(cacheKey, apiPath)) {
      return true;
    }
  }

  return false;
};

const dispatchInvalidation = (apiPath) => {
  if (typeof window === "undefined") {
    return;
  }

  window.dispatchEvent(
    new CustomEvent(CACHE_INVALIDATED_EVENT, {
      detail: { apiPath },
    }),
  );
};

export const shouldRefreshForInvalidation = (apiPath, invalidatedPath) =>
  matchesPrefix(apiPath, invalidatedPath) || matchesPrefix(invalidatedPath || "", apiPath);

const rootApiResource = (apiPath) => apiPath.split("?")[0].split("/")[0];
const shouldSampleCacheHit = () => Math.random() < CACHE_HIT_SAMPLE_RATE;

export const fetchWithCache = async (apiPath, params = {}, options = {}) => {
  const { ttl = DEFAULT_TTL_MS } = options;
  const cacheKey = buildCacheKey(apiPath, params);
  const now = Date.now();
  const cachedEntry = cache.get(cacheKey);
  const apiResource = rootApiResource(apiPath);

  if (cachedEntry && cachedEntry.expiresAt > now) {
    if (shouldSampleCacheHit()) {
      reportPerformanceMetric(
        "select_cache_hit",
        1,
        { api_resource: apiResource, cache_key: cacheKey, sample_rate: CACHE_HIT_SAMPLE_RATE },
        "api",
        "counter",
      );
    }
    return cachedEntry.data;
  }

  if (cachedEntry) {
    cache.delete(cacheKey);
  }

  if (pendingRequests.has(cacheKey)) {
    return pendingRequests.get(cacheKey);
  }

  const startedAt = now;
  const request = (async () => {
    try {
      const response = await fetch(`/api/v1/${cacheKey}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();

      reportPerformanceMetric(
        "select_fetch_ms",
        Date.now() - startedAt,
        {
          api_resource: apiResource,
          cache_key: cacheKey,
          request_kind: apiPath.includes("/") ? "detail" : "collection",
        },
        "api",
      );

      if (!wasInvalidatedAfterRequest(cacheKey, startedAt)) {
        cache.set(cacheKey, {
          data,
          expiresAt: startedAt + ttl,
        });
      }

      return data;
    } finally {
      pendingRequests.delete(cacheKey);
    }
  })();

  pendingRequests.set(cacheKey, request);
  return request;
};

export const clearCache = (apiPath = null) => {
  const now = Date.now();

  if (apiPath) {
    for (const key of cache.keys()) {
      if (matchesPrefix(key, apiPath)) {
        cache.delete(key);
      }
    }

    for (const key of pendingRequests.keys()) {
      if (matchesPrefix(key, apiPath)) {
        pendingRequests.delete(key);
      }
    }
  } else {
    cache.clear();
    pendingRequests.clear();
  }

  invalidatedAt.set(apiPath, now);
  dispatchInvalidation(apiPath);
};
